import React, { Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { RequireStaff } from '../components/auth/RequireStaff';

const SimulatorPage = React.lazy(() => import('../features/simulator/SimulatorPage'));

// ─── Lazy page imports ────────────────────────────────────────────────────────
const ForgotPasswordPage = React.lazy(() => import('../features/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../features/auth/ResetPasswordPage'));
const LoginPage = React.lazy(() => import('../features/auth/LoginPage'));
const ChangePasswordPage = React.lazy(() => import('../features/auth/ChangePasswordPage'));
const RequestPage = React.lazy(() => import('../features/public/RequestPage'));
const DashboardPage = React.lazy(() => import('../features/dashboard/DashboardPage'));
const AssetsPage = React.lazy(() => import('../features/assets/AssetsPage'));
const AssetDetailPage = React.lazy(() => import('../features/assets/AssetDetailPage'));
const PmSchedulePage = React.lazy(() => import('../features/pm/PmSchedulePage'));
const PmTaskDetailPage = React.lazy(() => import('../features/pm/PmTaskDetailPage'));
const WorkOrdersPage = React.lazy(() => import('../features/work-orders/WorkOrdersPage'));
const WorkOrderDetailPage = React.lazy(() => import('../features/work-orders/WorkOrderDetailPage'));
const InventoryPage = React.lazy(() => import('../features/inventory/InventoryPage'));
const TechniciansPage = React.lazy(() => import('../features/technicians/TechniciansPage'));
const TechnicianDetailPage = React.lazy(
  () => import('../features/technicians/TechnicianDetailPage'),
);
const ProjectsPage = React.lazy(() => import('../features/projects/ProjectsPage'));
const ProjectDetailPage = React.lazy(() => import('../features/projects/ProjectDetailPage'));
const QuotesPage = React.lazy(() => import('../features/quotes/QuotesPage'));
const QuoteDetailPage = React.lazy(() => import('../features/quotes/QuoteDetailPage'));
const InvoicesPage = React.lazy(() => import('../features/invoices/InvoicesPage'));
const InvoiceDetailPage = React.lazy(() => import('../features/invoices/InvoiceDetailPage'));
const ClientsPage = React.lazy(() => import('../features/clients/ClientsPage'));
const UsersPage = React.lazy(() => import('../features/settings/UsersPage'));
const RolePermissionsPage = React.lazy(() => import('../features/settings/RolePermissionsPage'));
const AuditLogPage = React.lazy(() => import('../features/settings/AuditLogPage'));
const ChangePasswordSettings = React.lazy(
  () => import('../features/settings/ChangePasswordSettings'),
);
const ReportsPage = React.lazy(() => import('../features/reports/ReportsPage'));
const InboxPage = React.lazy(() => import('../features/inbox/InboxPage'));
const TimeClockPage = React.lazy(() => import('../features/time-clock/TimeClockPage'));
const DispatchCalendarPage = React.lazy(() => import('../features/dispatch/DispatchCalendarPage'));

// ─── Loading spinner fallback ─────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// ─── ProtectedRoute ───────────────────────────────────────────────────────────
function ProtectedRoute() {
  return (
    <RequireStaff>
      <Suspense fallback={<PageLoader />}>
        <AppShell />
      </Suspense>
    </RequireStaff>
  );
}

// ─── Router definition ────────────────────────────────────────────────────────
export const appRoutes: RouteObject[] = [
  {
    path: '/simulator',
    element: (
      <RequireStaff>
        <Suspense fallback={<PageLoader />}>
          <SimulatorPage />
        </Suspense>
      </RequireStaff>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/change-password',
    element: (
      <Suspense fallback={<PageLoader />}>
        <ChangePasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/request',
    element: (
      <Suspense fallback={<PageLoader />}>
        <RequestPage />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/assets', element: <AssetsPage /> },
      { path: '/assets/:id', element: <AssetDetailPage /> },
      { path: '/pm', element: <PmSchedulePage /> },
      { path: '/pm/:id', element: <PmTaskDetailPage /> },
      { path: '/work-orders', element: <WorkOrdersPage /> },
      { path: '/work-orders/:id', element: <WorkOrderDetailPage /> },
      { path: '/inventory', element: <InventoryPage /> },
      { path: '/technicians', element: <TechniciansPage /> },
      { path: '/technicians/:id', element: <TechnicianDetailPage /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/:id', element: <ProjectDetailPage /> },
      { path: '/quotes', element: <QuotesPage /> },
      { path: '/quotes/:id', element: <QuoteDetailPage /> },
      { path: '/invoices', element: <InvoicesPage /> },
      { path: '/invoices/:id', element: <InvoiceDetailPage /> },
      { path: '/clients', element: <ClientsPage /> },
      { path: '/settings/users', element: <UsersPage /> },
      { path: '/settings/permissions', element: <RolePermissionsPage /> },
      { path: '/settings/audit-log', element: <AuditLogPage /> },
      { path: '/settings/password', element: <ChangePasswordSettings /> },
      { path: '/reports', element: <ReportsPage /> },
      { path: '/inbox', element: <InboxPage /> },
      { path: '/time-clock', element: <TimeClockPage /> },
      { path: '/dispatch', element: <DispatchCalendarPage /> },
    ],
  },
];
